import React from 'react';
import {v4 as uuidv4} from 'uuid';

import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin, StudyPhase, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeStudiesProceedToExecutionEventMetadata,
  STUDIES_PROCEED_TO_EXECUTION_ENDED_EVENT,
  STUDIES_PROCEED_TO_EXECUTION_STARTED_EVENT,
} from 'performance/processStudiesEvents';
import SetStudyPhaseDropdown, {
  SetStudyPhaseDropdownProps,
} from 'studies/workspace/overview/SetStudyPhaseDropdown';
import {ButtonSize} from 'ui-library/buttons/Button';

export const PROCEED_TO_EXECUTION_MESSAGE = (
  <>
    <p>
      Proceeding will lock your treatment groups, sample definitions, and sample testing plans. Returning the
      study to the design phase will allow you to add new information, but not edit existing content.
    </p>
    <p>Review and finalize your updates before continuing— this action is irreversible.</p>
  </>
);
export const PROCEDURE_PUBLISHING_PROCEED_TO_EXECUTION_MESSAGE = (
  <>
    <p>
      When you move a study to the execution phase, treatment groups, sample definitions, and sample test
      plans are locked. Sample test plans will be fixed to the latest published version of their assay, even
      if the study returns to the design phase. You will not be able to change the assay version.
    </p>
    <p>
      Moving a study back to design phase will allow you to add new information, but all existing content
      stays locked.
    </p>
    <p>Review and confirm your updates. This action can't be undone.</p>
  </>
);

export interface SetClinicalStudyPhaseDropdownProps extends SetStudyPhaseDropdownProps {
  actionOrigin: ActionOrigin; // required for perf testing
}

function logProceedToExecutionTiming(
  studyId: string,
  studyType: StudyType
): (actionOrigin: ActionOrigin) => void {
  const id = `studies-proceed-to-execution-${studyId}-${uuidv4()}`;
  logProfilerTimingEvent({...STUDIES_PROCEED_TO_EXECUTION_STARTED_EVENT, id});
  return (actionOrigin: ActionOrigin) => {
    logProfilerTimingEvent(
      {...STUDIES_PROCEED_TO_EXECUTION_ENDED_EVENT, id},
      composeStudiesProceedToExecutionEventMetadata(studyType, actionOrigin)
    );
  };
}

export default function SetClinicalStudyPhaseDropdown({
  study,
  refetch,
  isDisabled,
  requiresApproval,
  allowReturnToDesignPhase,
  buttonSize,
  confirmPhaseChange,
  actionOrigin,
}: SetClinicalStudyPhaseDropdownProps): JSX.Element {
  return (
    <SetStudyPhaseDropdown
      study={study}
      refetch={refetch}
      isDisabled={isDisabled}
      requiresApproval={requiresApproval}
      buttonSize={buttonSize ?? ButtonSize.SMALL}
      allowReturnToDesignPhase={allowReturnToDesignPhase}
      confirmPhaseChange={
        confirmPhaseChange ?? {
          [StudyPhase.DESIGN]: null,
          [StudyPhase.EXECUTION]: {
            forwardsMessage: BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING
              ? PROCEDURE_PUBLISHING_PROCEED_TO_EXECUTION_MESSAGE
              : PROCEED_TO_EXECUTION_MESSAGE,
          },
          [StudyPhase.COMPLETE]: null,
        }
      }
      actionOrigin={actionOrigin}
      proceedToExecutionLogger={logProceedToExecutionTiming}
    />
  );
}
